import NotificationsIcon from '@mui/icons-material/Notifications';
import DeleteIcon from '@mui/icons-material/Delete';
import {Badge, Box, IconButton, ListItem, ListItemText, Menu, Slide, styled, useTheme, Tooltip} from '@mui/material';
import useLanguage from 'hooks/useLanguage';
import {useState} from 'react';
import {TransitionGroup} from 'react-transition-group';
import useURLParser from 'hooks/useParseURL';
import {useCagesWithUnallocatedBiomass, useGetNotifications} from 'hooks/swr';
import backendCommunicator from 'services/backendCommunicator';
import Alert from 'helpers/Alert';
import {SWRKeyEnum, URLPathname} from 'interfaces';
import {useSWRConfig} from 'swr';
import {useTranslation} from 'react-i18next';
import ErrorIcon from '@mui/icons-material/Error';
import {grey} from '@mui/material/colors';

export const AppTopBarNotificationList = () => {
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const {t} = useTranslation('common');
  const {dateFormatter} = useLanguage();
  const {planId, navigate, urlSearchParams} = useURLParser();

  const {data} = useGetNotifications({resourceId: planId, localOptions: {use: undefined}});
  const {data: cagesWithUnallocatedBiomass} = useCagesWithUnallocatedBiomass({resourceId: planId});
  const {mutate} = useSWRConfig();
  const theme = useTheme();

  const hasNewNotifications = data && data?.filter((notification) => !notification.read).length > 0;
  const hasUnallocatedBiomass = !!cagesWithUnallocatedBiomass?.length;
  const hasNotifications = !!(data && data.length > 0) || hasUnallocatedBiomass;

  const sortedNotifications = data?.slice().sort((a, b) => {
    if (a.sentAt && b.sentAt) {
      return new Date(b.sentAt).getTime() - new Date(a.sentAt).getTime();
    } else {
      return 0;
    }
  });

  const handleClick = (event) => {
    if (!hasNotifications) {
      return;
    }
    setAnchorEl(event.currentTarget);
    markAllAsRead();
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const ITEM_HEIGHT = 60;
  const maxNotifications = 5;

  const remove = (notificationId: string) => {
    const newNotifications = data?.filter((notification) => notification.id !== notificationId);
    void mutate([planId, SWRKeyEnum.GET_NOTIFICATIONS], newNotifications);
    if (newNotifications && newNotifications?.length === 0) {
      handleClose();
    }
    backendCommunicator
      .deleteNotification(notificationId)
      .catch((error) => {
        Alert.error(error);
      })
      .finally(() => {
        void mutate([planId, SWRKeyEnum.GET_NOTIFICATIONS]);
      });
  };

  const markAllAsRead = () => {
    if (!data) {
      return;
    }
    const readNotifications = data
      .filter((notification) => !notification.read)
      .map((notification) => {
        return {id: notification.id, read: true};
      });
    if (readNotifications.length > 0) {
      backendCommunicator
        .batchUpdateNotifications(readNotifications)
        .catch((error) => {
          Alert.error(error);
        })
        .finally(() => {
          void mutate([planId, SWRKeyEnum.GET_NOTIFICATIONS]);
        });
    }
  };

  return (
    <>
      <Tooltip title={t('notifications') ?? ''}>
        <Badge variant="dot" color="primary" overlap="circular" invisible={!hasNewNotifications}>
          <IconButton
            size="small"
            onClick={handleClick}
            color={hasUnallocatedBiomass ? 'error' : 'inherit'}
            disabled={!hasNotifications}
            sx={{width: 40}}
          >
            {hasUnallocatedBiomass ? <AnimatedNotificationIcon /> : <NotificationsIcon />}
          </IconButton>
        </Badge>
      </Tooltip>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        sx={{zIndex: 3000}}
        slotProps={{
          paper: {
            style: {
              maxHeight: ITEM_HEIGHT * maxNotifications + 16,
              width: '35ch',
            },
          },
        }}
      >
        {hasUnallocatedBiomass && (
          <ErrorListItem>
            <Box display="flex" width="100%" alignItems="center">
              <ListItemText
                onClick={() => {
                  navigate({pathname: URLPathname.concessions, search: urlSearchParams.toString()});
                }}
              >
                {t('unallocatedBiomassNotification')}
              </ListItemText>
              <ErrorIcon />
            </Box>
          </ErrorListItem>
        )}
        <TransitionGroup>
          {sortedNotifications &&
            sortedNotifications.map((notification) => {
              return (
                <Slide direction="left" key={notification.id} appear={false}>
                  <HoverListItem sx={{py: 0}}>
                    <ListItemText
                      primary={t(notification.messageCode)}
                      secondary={dateFormatter.toLimitedTimeAgo(new Date(notification.sentAt))}
                      slotProps={{
                        primary: {color: !notification.read ? theme.palette.primary.main : undefined},
                        secondary: {color: 'text.disabled'},
                      }}
                    />
                    <IconButton
                      onClick={() => {
                        remove(notification.id);
                      }}
                      aria-label="delete"
                    >
                      <DeleteIcon />
                    </IconButton>
                  </HoverListItem>
                </Slide>
              );
            })}
        </TransitionGroup>
      </Menu>
    </>
  );
};

const ErrorListItem = styled(ListItem)(({theme}) => ({
  background: theme.palette.error.main,
  cursor: 'pointer',
  color: grey[50],
}));

const HoverListItem = styled(ListItem)(({theme}) => ({
  cursor: 'default',
  '&:hover, &:active': {
    backgroundColor: theme.palette.action.selected,
    color: theme.palette.primary.main,
  },
}));

const AnimatedNotificationIcon = () => (
  <NotificationsIcon
    sx={{
      animation: 'ring 6s .7s ease-in-out infinite',
      '@keyframes ring': {
        '0%': {transform: 'rotate(0)'},
        '1%': {transform: 'rotate(30deg)'},
        '3%': {transform: 'rotate(-28deg)'},
        '5%': {transform: 'rotate(34deg)'},
        '7%': {transform: 'rotate(-32deg)'},
        '9%': {transform: 'rotate(30deg)'},
        '11%': {transform: 'rotate(-28deg)'},
        '13%': {transform: 'rotate(26deg)'},
        '15%': {transform: 'rotate(-24deg)'},
        '17%': {transform: 'rotate(22deg)'},
        '19%': {transform: 'rotate(-20deg)'},
        '21%': {transform: 'rotate(18deg)'},
        '23%': {transform: 'rotate(-16deg)'},
        '25%': {transform: 'rotate(14deg)'},
        '27%': {transform: 'rotate(-12deg)'},
        '29%': {transform: 'rotate(10deg)'},
        '31%': {transform: 'rotate(-8deg)'},
        '33%': {transform: 'rotate(6deg)'},
        '35%': {transform: 'rotate(-4deg)'},
        '37%': {transform: 'rotate(2deg)'},
        '39%': {transform: 'rotate(-1deg)'},
        '41%': {transform: 'rotate(1deg)'},
        '43%': {transform: 'rotate(0)'},
        '100%': {transform: 'rotate(0)'},
      },
    }}
    fontSize="medium"
    color="error"
  />
);
